import { isValidUuid } from '@/util/is-valid-uuid'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react'
import { useStore } from 'react-redux'
import { useParams } from 'react-router-dom'

import { selectActiveMatterArtifactSelection, selectMatterArtifactSelectionAttachment } from '@/agent-v2/artifact-selection/store/selectors'
import { MatterArtifactSelectionActions } from '@/agent-v2/artifact-selection/store/slice'
import type { MatterArtifactSelectionAttachment } from '@/agent-v2/artifact-selection/types'
import { useIsAgentV2ConversationCreator } from '@/agent-v2/conversations/hooks/useIsAgentV2ConversationCreator'
import { selectAgentV2RunPhase, selectAgentV2SendableChatFileIds, selectAgentV2SendableChatWritingSampleFileId } from '@/agent-v2/conversations/store/selectors'
import { AgentV2RunPhase } from '@/agent-v2/conversations/store/slice'
import { buildAgentV2ReferenceViewerSession } from '@/agent-v2/references/build-agent-v2-reference-viewer-session'
import { getAgentV2ExternalReferenceUrl } from '@/agent-v2/references/agent-v2-reference-opening'
import { useAgentV2ArtifactEditorAnalytics } from '@/agent-v2/artifact-editor/hooks/useAgentV2ArtifactEditorAnalytics'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { CitatorOpenedIn, CitatorPanelSource, CitatorSurface, mapCaseStatusToBadgeType } from '@/analytics/enums/citator.enums'
import { RiaCitatorSection } from '@/analytics/enums/ria.enums'
import { trackCitatorBadgeClicked, trackCitatorBadgeImpression, trackCitatorPanelOpened } from '@/analytics/utils/citator-analytics'
import { CaseStatusEnum, isWarningCaseStatus, type WarningCaseStatus } from '@/citator/schema/case-status-schema'
import { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { selectAgentV2ReferencesByCitationId } from '@/agent-v2/references/store/selectors'
import { AgentV2TransportActions } from '@/agent-v2/transport/store/slice'
import { selectAgentV2TransportCanStopOrAbort } from '@/agent-v2/transport/store/selectors'
import { buildAgentV2SearchDefaultsFromAgentFilters } from '@/agent-v2/utils/agent-v2-search-defaults'
import { kAgentV2MaxTurnInputChars } from '@/agent-v2/utils/agent-v2-contract'
import {
  selectAgentConversationFormCaselawSearchFilter,
  selectAgentConversationFormIncludeUnpublishedCases,
  selectAgentConversationFormLRRSearchFilter,
  selectAgentConversationFormWebSearchEnabled,
} from '@/agent-v2/chat-form/store/selectors'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import type { MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { selectCurrentMatterArtifactVersionFromVersionId, selectMatterArtifactVersionById } from '@/matter-artifacts/store/selectors'
import { MatterArtifactsActions } from '@/matter-artifacts/store/slice'
import { resolveStandaloneViewerUrlFromSession } from '@/standalone-reference-viewer/utils/resolve-standalone-viewer-url'
import { openStandaloneViewerTab } from '@/standalone-reference-viewer/utils/open-standalone-viewer-tab'
import { useOpenAgentV2ReferenceViewer } from '@/agent-v2/hooks/useOpenAgentV2ReferenceViewer'
import type { OpenAgentV2ReferenceResult } from '@/agent-v2/hooks/useOpenAgentV2Reference'
import { useSourceOpensInChatPanel } from '@/drafting/workspace/hooks/useSourceOpensInChatPanel'
import { useDraftActiveCitationId } from '@/drafting/workspace/hooks/useDraftActiveCitationId'
import { useSyncDraftFootnoteNumbers } from '@/drafting/workspace/hooks/useSyncDraftFootnoteNumbers'
import type { RootState } from '@/store/store'
import { useAppDispatch } from '@/store/store-hooks'
import { useAppSelector } from '@/store/store-hooks'
import { useAgentV2ArtifactEditor } from '../hooks/useAgentV2ArtifactEditor'
import { EditorSaveStatusType } from '../types'
import AgentV2MarkdownEditor from './AgentV2MarkdownEditor'

type AgentV2ArtifactEditorProps = {
  conversationId: string
  matterArtifactVersionId: string
  pendingMatterSelection?: MatterArtifactSelectionAttachment | null
  /** Optional trailing toolbar slot, rendered next to the save status (e.g. the Add Template button). */
  templateButton?: ReactNode
}

/**
 * Result of flushing pending editor content before a caller acts on the latest saved head.
 * 'flushed' carries the id of a version newly persisted by this flush, or null when nothing newer
 * was saved (the caller falls back to its own resolved head version id); 'failed' means unsaved
 * edits could not be persisted and the caller must not proceed.
 */
export type AgentV2ArtifactEditorFlushResult = { status: 'flushed'; matterArtifactVersionId: string | null } | { status: 'failed' }

export interface AgentV2ArtifactEditorRef {
  /**
   * Flushes any pending editor content to a saved version (joining an in-flight save) and resolves
   * a discriminated result. Callers that must act on the latest content (e.g. exports) await this
   * before reading the head, and abort on 'failed'.
   */
  flushPendingSave: () => Promise<AgentV2ArtifactEditorFlushResult>
}

const AgentV2ArtifactEditor = forwardRef<AgentV2ArtifactEditorRef, AgentV2ArtifactEditorProps>(function AgentV2ArtifactEditor(
  { conversationId, matterArtifactVersionId, pendingMatterSelection, templateButton },
  ref
) {
  const currentMatterArtifactVersion = useAppSelector((state: RootState) => selectCurrentMatterArtifactVersionFromVersionId(state, matterArtifactVersionId))

  if (!currentMatterArtifactVersion) {
    return <ContentViewerErrorState message="Failed to load markdown content." />
  }

  return (
    <ResolvedAgentV2ArtifactEditor
      ref={ref}
      conversationId={conversationId}
      matterArtifactId={currentMatterArtifactVersion.matter_artifact_id}
      currentMatterArtifactVersion={currentMatterArtifactVersion}
      pendingMatterSelection={pendingMatterSelection}
      templateButton={templateButton}
    />
  )
})

type ResolvedAgentV2ArtifactEditorProps = {
  conversationId: string
  matterArtifactId: string
  currentMatterArtifactVersion: MatterArtifactVersionRead
  pendingMatterSelection?: MatterArtifactSelectionAttachment | null
  templateButton?: ReactNode
}

const ResolvedAgentV2ArtifactEditor = forwardRef<AgentV2ArtifactEditorRef, ResolvedAgentV2ArtifactEditorProps>(function ResolvedAgentV2ArtifactEditor(
  { conversationId, matterArtifactId, currentMatterArtifactVersion, pendingMatterSelection, templateButton },
  ref
) {
  const { matterId } = useParams<{ matterId: string }>()
  const dispatch = useAppDispatch()
  const sourceOpensInChatPanel = useSourceOpensInChatPanel()
  const openReferenceViewer = useOpenAgentV2ReferenceViewer()
  const store = useStore<RootState>()
  const referencesByCitationId = useAppSelector((state: RootState) => selectAgentV2ReferencesByCitationId(state, { conversationId }))
  const activeCitationId = useDraftActiveCitationId()
  const attachedMatterSelection = useAppSelector((state: RootState) => selectMatterArtifactSelectionAttachment(state, { conversationId }))
  const activeMatterSelection = useAppSelector((state: RootState) => selectActiveMatterArtifactSelection(state, { conversationId }))
  const runPhase = useAppSelector((state: RootState) => selectAgentV2RunPhase(state, { conversationId }))
  const transportCanStopOrAbort = useAppSelector((state: RootState) => selectAgentV2TransportCanStopOrAbort(state, { conversationId }))
  const isConversationCreator = useIsAgentV2ConversationCreator(conversationId)
  const caselawSearchFilter = useAppSelector((state: RootState) => selectAgentConversationFormCaselawSearchFilter(state, { conversationId }))
  const lrrSearchFilter = useAppSelector((state: RootState) => selectAgentConversationFormLRRSearchFilter(state, { conversationId }))
  const includeUnpublishedCases = useAppSelector((state: RootState) => selectAgentConversationFormIncludeUnpublishedCases(state, { conversationId }))
  // An edit sends the chat's files and leaves files waiting in the composer for the composer's own message
  const selectedFileIds = useAppSelector((state: RootState) => selectAgentV2SendableChatFileIds(state, { conversationId }))
  const writingSampleFileId = useAppSelector((state: RootState) => selectAgentV2SendableChatWritingSampleFileId(state, { conversationId }))
  const webSearchEnabled = useAppSelector((state: RootState) => selectAgentConversationFormWebSearchEnabled(state, { conversationId }))
  const selectedEditIsRunning = activeMatterSelection?.matter_artifact_id === currentMatterArtifactVersion.matter_artifact_id
  const handleSavedVersion = useCallback(
    (matterArtifactVersion: MatterArtifactVersionRead) => {
      dispatch(MatterArtifactsActions.upsertMatterArtifactVersion(matterArtifactVersion))
    },
    [dispatch]
  )
  const openStandaloneViewer = useCallback(
    (citationId: string, options?: { defaultTab?: CaseLawReferenceTab; defaultOpenSection?: RiaCitatorSection }): boolean => {
      const reference = referencesByCitationId[citationId]
      if (!reference) return false
      const externalUrl = getAgentV2ExternalReferenceUrl(reference)
      if (externalUrl) return openStandaloneViewerTab(externalUrl)

      const session = buildAgentV2ReferenceViewerSession(reference)
      const viewerUrl = resolveStandaloneViewerUrlFromSession(
        session,
        {
          conversationId,
          messageId: reference.turn_id,
          matterId,
        },
        options
      )
      if (!viewerUrl) return false

      return openStandaloneViewerTab(viewerUrl)
    },
    [conversationId, matterId, referencesByCitationId]
  )
  // In the draft workspace a source opens in the chat panel's source tab when the panel can show it, and in
  // a new browser tab when it cannot (see useSourceOpensInChatPanel). Everywhere else this editor renders,
  // in a new browser tab.
  const openSource = useCallback(
    (citationId: string, options?: { defaultTab?: CaseLawReferenceTab; defaultOpenSection?: RiaCitatorSection }): OpenAgentV2ReferenceResult => {
      const reference = referencesByCitationId[citationId]
      if (!sourceOpensInChatPanel || (reference && getAgentV2ExternalReferenceUrl(reference))) return openStandaloneViewer(citationId, options) && 'tab'

      return reference !== undefined && openReferenceViewer(reference, undefined, options) && 'panel'
    },
    [openReferenceViewer, openStandaloneViewer, referencesByCitationId, sourceOpensInChatPanel]
  )
  const handleCitationClick = useCallback((citationId: string) => openSource(citationId), [openSource])

  // Canvas flag analytics live here: the flag is a plain-DOM ProseMirror widget with no
  // hook access, so the host owns both click and impression events (unlike chat, where
  // the React flag component fires its own). Flag click-through deep-links to Citation
  // Analysis (the PRD's two-destinations rule: flag → treatment, citation text → source),
  // so a successful open counts as the citator detail being reached.
  const { trackEvent } = useAnalytics()
  const handleTreatmentFlagClick = useCallback(
    (citationId: string) => {
      const reference = referencesByCitationId[citationId]
      const caseStatus = reference?.source_type === 'caselaw' ? reference.case_status : null
      if (isWarningCaseStatus(caseStatus)) {
        trackCitatorBadgeClicked({ trackEvent, surface: CitatorSurface.DraftingInline, badgeType: mapCaseStatusToBadgeType(caseStatus) })
      }
      const opened = openSource(citationId, {
        defaultTab: CaseLawReferenceTab.citator,
        // Overruling risk opens with its case-citations section expanded, matching chat.
        defaultOpenSection: caseStatus === CaseStatusEnum.overruling_risk ? RiaCitatorSection.CaseCitations : undefined,
      })
      if (opened)
        trackCitatorPanelOpened({
          trackEvent,
          surface: CitatorSurface.DraftingInline,
          source: CitatorPanelSource.BadgeClick,
          openedIn: opened === 'panel' ? CitatorOpenedIn.ContentViewer : CitatorOpenedIn.StandaloneViewer,
        })
    },
    [openSource, referencesByCitationId, trackEvent]
  )
  const handleTreatmentFlagImpression = useCallback(
    (_citationId: string, caseStatus: WarningCaseStatus) => {
      trackCitatorBadgeImpression({ trackEvent, surface: CitatorSurface.DraftingInline, badgeType: mapCaseStatusToBadgeType(caseStatus) })
    },
    [trackEvent]
  )
  // Analytics needs values the editor hook returns, and the editor hook needs the save observer.
  // This ref breaks that cycle so neither hook depends on the other's call order.
  const trackAutoSaveSuccessRef = useRef<(saved: { matterArtifactVersionId: string; contentLength: number }) => void>(() => {})
  const {
    markdown,
    isLoading,
    hasLoadError,
    saveStatus,
    activeMatterArtifactVersionId,
    handleMarkdownChange,
    handleBlur,
    saveLatestContent,
    retryLoad,
    retrySave,
    resolveConflict,
    getActiveMatterArtifactVersionId,
  } = useAgentV2ArtifactEditor({
    matterArtifactId,
    currentMatterArtifactVersionId: currentMatterArtifactVersion.id,
    onSavedVersion: handleSavedVersion,
    onContentSaved: (saved) => trackAutoSaveSuccessRef.current(saved),
  })
  const { trackFirstEdit, trackAutoSaveSuccess, trackFormattingUsed, trackClipboardCopy } = useAgentV2ArtifactEditorAnalytics({
    matterArtifactId,
    matterArtifactVersionId: activeMatterArtifactVersionId,
    conversationId,
    matterId,
    isContentReady: !isLoading && markdown !== null,
    contentLength: markdown?.length ?? 0,
  })
  useEffect(() => {
    trackAutoSaveSuccessRef.current = trackAutoSaveSuccess
  }, [trackAutoSaveSuccess])
  // From the markdown the editor last parsed, not the live one, so the tab matches the footnote numbers the editor rendered.
  const [parsedMarkdown, setParsedMarkdown] = useState<string | null>(null)
  useSyncDraftFootnoteNumbers({ markdown: parsedMarkdown, referencesByCitationId })
  const isAdoptingCurrentHead =
    markdown !== null &&
    activeMatterArtifactVersionId !== currentMatterArtifactVersion.id &&
    !hasLoadError &&
    (saveStatus === EditorSaveStatusType.IDLE || saveStatus === EditorSaveStatusType.SAVED)
  // The same flush-then-read sequence the selection-edit submit uses: saveLatestContent joins an
  // in-flight save and drains queued edits, so the ref read is the settled head afterwards.
  useImperativeHandle(
    ref,
    () => ({
      flushPendingSave: async () => {
        // Only a version persisted by this flush is reported; a no-op flush (nothing to save)
        // resolves null so the caller keeps using its already resolved current head.
        const before = getActiveMatterArtifactVersionId()
        const saved = await saveLatestContent()
        if (saved) {
          const after = getActiveMatterArtifactVersionId()
          return { status: 'flushed', matterArtifactVersionId: after !== before ? after : null }
        }
        // Nothing loaded means no edits were possible — the caller's own version id is safe.
        if (getActiveMatterArtifactVersionId() === null) return { status: 'flushed', matterArtifactVersionId: null }
        return { status: 'failed' }
      },
    }),
    [saveLatestContent, getActiveMatterArtifactVersionId]
  )
  const activeMatterArtifactVersion = useAppSelector((state: RootState) => selectMatterArtifactVersionById(state, activeMatterArtifactVersionId))
  // A completed autosave lands in two renders: the store demotes the old head one render before
  // this component's active-version state catches up. Computing read-only from that mixed state
  // revoked contenteditable for one frame, which dropped the user's focus and caret on every
  // autosave. An in-flight save may not flip the editor read-only; a genuinely stale base still
  // locks via the conflict path when the save rejects.
  const editorIsReadOnly = selectedEditIsRunning || (activeMatterArtifactVersion?.is_current !== true && saveStatus !== EditorSaveStatusType.SAVING)
  const matterSelection = pendingMatterSelection ?? attachedMatterSelection
  const displayedMatterSelection = matterSelection?.selection.matter_artifact_version_id === activeMatterArtifactVersionId ? matterSelection : null
  const clearAttachedMatterSelection = useCallback(() => {
    dispatch(MatterArtifactSelectionActions.clearMatterArtifactSelection({ conversationId }))
  }, [conversationId, dispatch])
  const handleSubmitMatterSelection = useCallback(
    async (attachment: MatterArtifactSelectionAttachment, instruction: string): Promise<boolean> => {
      const trimmedInstruction = instruction.trim()
      if (
        !isConversationCreator ||
        !isValidUuid(conversationId) ||
        !isValidUuid(matterId) ||
        !trimmedInstruction ||
        trimmedInstruction.length > kAgentV2MaxTurnInputChars ||
        editorIsReadOnly ||
        runPhase !== AgentV2RunPhase.IDLE ||
        transportCanStopOrAbort
      ) {
        return false
      }

      const saved = await saveLatestContent()
      if (!saved) return false
      const savedMatterArtifactVersionId = getActiveMatterArtifactVersionId()
      if (!savedMatterArtifactVersionId) return false
      const latestState = store.getState()
      if (
        selectAgentV2RunPhase(latestState, { conversationId }) !== AgentV2RunPhase.IDLE ||
        selectAgentV2TransportCanStopOrAbort(latestState, { conversationId })
      ) {
        return false
      }

      const savedAttachment: MatterArtifactSelectionAttachment = {
        ...attachment,
        selection: {
          ...attachment.selection,
          matter_artifact_version_id: savedMatterArtifactVersionId,
        },
      }
      dispatch(
        MatterArtifactSelectionActions.attachMatterArtifactSelection({
          conversationId,
          attachment: savedAttachment,
        })
      )
      dispatch(
        AgentV2TransportActions.startTurnRequested({
          assistantMessageId: crypto.randomUUID(),
          clientRequestId: crypto.randomUUID(),
          conversationId,
          inputText: trimmedInstruction,
          matterId,
          matterArtifactSelection: savedAttachment.selection,
          searchDefaults: buildAgentV2SearchDefaultsFromAgentFilters({
            caselawSearchFilter,
            includeUnpublishedCases,
            lrrSearchFilter,
            fileIds: selectedFileIds,
            writingSampleFileId,
            webSearchEnabled,
          }),
          userMessageId: crypto.randomUUID(),
        })
      )
      return true
    },
    [
      caselawSearchFilter,
      conversationId,
      dispatch,
      editorIsReadOnly,
      includeUnpublishedCases,
      isConversationCreator,
      lrrSearchFilter,
      matterId,
      runPhase,
      getActiveMatterArtifactVersionId,
      saveLatestContent,
      selectedFileIds,
      writingSampleFileId,
      webSearchEnabled,
      store,
      transportCanStopOrAbort,
    ]
  )
  const handleEditorMarkdownChange = useCallback(
    (nextMarkdown: string) => {
      if (editorIsReadOnly) return
      trackFirstEdit()
      if (displayedMatterSelection) clearAttachedMatterSelection()
      handleMarkdownChange(nextMarkdown)
    },
    [clearAttachedMatterSelection, displayedMatterSelection, editorIsReadOnly, handleMarkdownChange, trackFirstEdit]
  )

  if (isLoading) return <ContentViewerLoadingSpinner size="large" />
  if (hasLoadError || markdown === null) return <ContentViewerErrorState message="Failed to load markdown content." onRetry={retryLoad} />

  return (
    <div className="relative h-full min-h-0 overflow-hidden">
      <AgentV2MarkdownEditor
        initialMarkdown={markdown}
        onDocumentParsed={setParsedMarkdown}
        onChange={handleEditorMarkdownChange}
        onBlur={editorIsReadOnly ? undefined : handleBlur}
        readOnly={editorIsReadOnly}
        showMenu
        saveStatus={saveStatus}
        onRetry={saveStatus === EditorSaveStatusType.CONFLICT ? resolveConflict : retrySave}
        referencesByCitationId={referencesByCitationId}
        // openSource below opens in the chat panel exactly when the panel can show a source.
        referencesOpenInPanel={sourceOpensInChatPanel}
        activeCitationId={activeCitationId}
        onCitationClick={handleCitationClick}
        onTreatmentFlagClick={handleTreatmentFlagClick}
        onTreatmentFlagImpression={handleTreatmentFlagImpression}
        matterArtifactId={matterArtifactId}
        matterArtifactVersionId={activeMatterArtifactVersionId}
        attachedMatterSelection={displayedMatterSelection}
        onFormatUsed={trackFormattingUsed}
        onCopy={trackClipboardCopy}
        onSubmitMatterSelection={handleSubmitMatterSelection}
        onAttachedMatterSelectionInvalid={clearAttachedMatterSelection}
        templateButton={templateButton}
      />
      {isAdoptingCurrentHead ? (
        <div
          aria-label="Loading latest draft"
          className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 text-sm font-medium text-brand-neutral-700"
          role="status"
        >
          Loading latest draft...
        </div>
      ) : null}
    </div>
  )
})

export default AgentV2ArtifactEditor
